// hoursFormat.ts — per-tenant hours display format (TAS-75).
//
// 2020PH reads hours as decimals (41.25); WCDP and NPM read them off a clock
// (41:15). The tenant config decides, and an ABSENT key means 'decimal' so that
// omitting it can never move 2020PH.
//
// Minutes are the source of truth. HH:MM is always derived from a minute count,
// never from a decimal-hour string that has already been rounded — 41.25 hrs is
// 2475 minutes is "41:15", and re-rounding via `.toFixed(2)` first would lose
// the exact minute. Totals over 24 hours stay plain H:MM ("298:21" for a crew
// roll-up); this formatter never carves days out of a total.
//
// This module formats HOURS ONLY. Dollars — claimed amounts, piece average
// rate ($/hr, still computed from decimal hours) — are untouched by the flag.

import config from '../configLoader';

export type HoursDisplayFormat = 'hhmm' | 'decimal';

export const DEFAULT_HOURS_DISPLAY_FORMAT: HoursDisplayFormat = 'decimal';

/** The active tenant's format. Anything but an explicit 'hhmm' reads as decimal. */
export function hoursDisplayFormat(): HoursDisplayFormat {
  return config.hoursDisplayFormat === 'hhmm' ? 'hhmm' : DEFAULT_HOURS_DISPLAY_FORMAT;
}

/** Pure core: format a minute count in an explicit mode. `decimals` applies to
 *  decimal mode only — HH:MM has no fractional part to size. */
export function formatMinutesAs(
  minutes: number,
  format: HoursDisplayFormat,
  decimals: number = 2,
): string {
  const mins = Number.isFinite(minutes) ? minutes : 0;
  if (format === 'decimal') return (mins / 60).toFixed(decimals);
  const sign = mins < 0 ? '-' : '';
  const whole = Math.round(Math.abs(mins));
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return `${sign}${h}:${m.toString().padStart(2, '0')}`;
}

/** Pure core for call sites that already hold DECIMAL HOURS rather than minutes.
 *  Decimal mode keeps the caller's own number so the 2020PH rendering is
 *  bit-for-bit what it was before TAS-75; only HH:MM converts to minutes. */
export function formatDecimalHoursAs(
  hours: number,
  format: HoursDisplayFormat,
  decimals: number = 2,
): string {
  const h = Number.isFinite(hours) ? hours : 0;
  if (format === 'decimal') return h.toFixed(decimals);
  return formatMinutesAs(Math.round(h * 60), 'hhmm');
}

/** Format a minute count for the active tenant. Preferred entry point — pass minutes. */
export function formatHours(minutes: number, decimals: number = 2): string {
  return formatMinutesAs(minutes, hoursDisplayFormat(), decimals);
}

/** Format decimal hours for the active tenant. For legacy call sites whose upstream
 *  value is already hours; prefer `formatHours` with minutes wherever minutes exist. */
export function formatDecimalHours(hours: number, decimals: number = 2): string {
  return formatDecimalHoursAs(hours, hoursDisplayFormat(), decimals);
}

/** Decimal hours for a call site that today prints the raw number with NO fixed
 *  decimal places (a "8 hrs" time-off chip, not "8.00 hrs"). Decimal mode keeps that
 *  rendering untouched so 2020PH sees exactly what it saw before; HH:MM converts. */
export function formatDecimalHoursNatural(hours: number): string {
  return hoursDisplayFormat() === 'hhmm' ? formatDecimalHoursAs(hours, 'hhmm') : String(hours);
}
